/**
 * Hook to fetch organisation timesheet settings
 * Routes to API (live mode) or localStorage (demo mode) based on screen data mode
 */

import { useState, useEffect, useCallback } from 'react';
import { useDataSource } from '@/contexts/DataSourceContext';
import { getOrganisationTimesheetSettings, getCachedLiveOrgSettings, setCachedLiveOrgSettings } from '@/services/settingsRepository';
import { DEFAULT_TIMESHEET_SETTINGS } from '@/types/timesheetSettings';
import type { OrganisationTimesheetSettings } from '@/types/timesheetSettings';

export interface UseOrganisationSettingsReturn {
  settings: OrganisationTimesheetSettings;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to get organisation timesheet settings based on screen data mode
 * 
 * @param screenId - The ID of the screen (e.g., 'homepage', 'timesheet')
 * @returns Object containing settings, loading state, error, and refetch function
 * 
 * Usage:
 * ```tsx
 * const { settings, loading, error } = useOrganisationSettings('homepage');
 * 
 * // Access deadline settings
 * const { deadlineDay, deadlineTime } = settings.deadline;
 * ```
 */
export function useOrganisationSettings(screenId: string): UseOrganisationSettingsReturn {
  const { getScreenMode } = useDataSource();
  
  // Initialise with cached value for live mode, or default for demo mode
  // This ensures the first render uses the cached value (e.g., 30h) instead of the default (40h)
  const [settings, setSettings] = useState<OrganisationTimesheetSettings>(() => {
    const mode = getScreenMode(screenId);
    if (mode === 'live') {
      const cached = getCachedLiveOrgSettings();
      // If we have a cached value, use it; otherwise fall back to default
      return cached ?? DEFAULT_TIMESHEET_SETTINGS;
    }
    // Demo mode uses default settings
    return DEFAULT_TIMESHEET_SETTINGS;
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchSettings = useCallback(async () => {
    const mode = getScreenMode(screenId);
    
    try {
      setLoading(true);
      setError(null);
      
      const fetchedSettings = await getOrganisationTimesheetSettings(mode);
      setSettings(fetchedSettings);
      
      // Cache is updated inside getOrganisationTimesheetSettings for live mode
    } catch (err) {
      console.error('Error fetching organisation settings:', err);
      setError(err as Error);
      // Fall back to default settings on error
      setSettings(DEFAULT_TIMESHEET_SETTINGS);
    } finally {
      setLoading(false);
    }
  }, [screenId, getScreenMode]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  return { settings, loading, error, refetch: fetchSettings };
}
